import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Mail, Phone, MapPin, Shield, Eye, Lock } from 'lucide-react';
import SEO from '../components/SEO';
import { useLocalization } from '../hooks/useLocalization';

const PrivacyPolicyPage: React.FC = () => {
  const { t } = useLocalization();
  const lastUpdated = "December 31, 2024";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Privacy Policy"
        description="Evoltbay Privacy Policy. How we collect, use, and protect your information on our solar energy marketplace and enterprise quotation platform."
        keywords="privacy policy, Evoltbay privacy, data protection, solar marketplace"
        url="https://evoltbay.com/privacy"
      />
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ArrowLeft size={20} />
              {t('privacy.backToEvoltbay')}
            </Link>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="text-blue-600" size={32} />
            <h1 className="text-3xl font-bold text-gray-900">{t('privacy.title')}</h1>
          </div>
          <div className="flex items-center gap-2 text-gray-600 mt-2">
            <Calendar size={16} />
            <span>{t('privacy.lastUpdated')}: {lastUpdated}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Evoltbay Inc. ("Evoltbay," "we," "us," or "our") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              solar energy marketplace platform, website, mobile application, and related services (collectively, the "Service").
            </p>
            <p className="text-gray-700 leading-relaxed">
              Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, 
              please do not access the Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Eye size={20} className="text-blue-600" />
              2.1 Personal Information
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Register for an account</li>
              <li>Make a purchase or sale</li>
              <li>Submit a quote request</li>
              <li>Contact customer support</li>
              <li>Subscribe to our newsletter</li>
              <li>Participate in surveys or promotions</li>
            </ul>

            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h4 className="font-medium text-gray-900 mb-2">Personal Information Includes:</h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
                <ul className="space-y-1">
                  <li>• Full name</li>
                  <li>• Email address</li>
                  <li>• Phone number</li>
                  <li>• Mailing address</li>
                  <li>• Company information</li>
                </ul>
                <ul className="space-y-1">
                  <li>• Payment information</li>
                  <li>• Government certifications</li>
                  <li>• Professional licenses</li>
                  <li>• Tax identification numbers</li>
                  <li>• Profile photos</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-medium text-gray-900 mb-3">2.2 Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you access our Service, we may automatically collect certain information about your device and usage patterns:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>IP address and geolocation data</li>
              <li>Browser type and version</li>
              <li>Operating system information</li>
              <li>Pages visited and time spent</li>
              <li>Referral sources</li>
              <li>Device identifiers</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-900 mb-3">2.3 Cookies and Tracking Technologies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies, web beacons, and similar tracking technologies to enhance your experience and collect usage data. 
              You can control cookie preferences through your browser settings.
            </p>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect for various purposes, including:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Service Operations</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                  <li>Account creation and management</li>
                  <li>Processing transactions and payments</li>
                  <li>Facilitating marketplace interactions</li>
                  <li>Providing customer support</li>
                  <li>Verifying vendor credentials</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Communication & Marketing</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                  <li>Sending transactional emails</li>
                  <li>Marketing communications (with consent)</li>
                  <li>Platform updates and notifications</li>
                  <li>Survey and feedback requests</li>
                  <li>Promotional offers</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Platform Improvement</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                  <li>Analytics and usage patterns</li>
                  <li>Feature development</li>
                  <li>Performance optimization</li>
                  <li>Security monitoring</li>
                  <li>Fraud prevention</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Legal Compliance</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                  <li>Regulatory compliance</li>
                  <li>Legal obligations</li>
                  <li>Dispute resolution</li>
                  <li>Government requests</li>
                  <li>Terms enforcement</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How We Share Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may share your information in the following circumstances:
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 With Other Users</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              To facilitate marketplace transactions, we share relevant information between buyers and vendors, 
              including contact details, company information, and transaction history.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 Service Providers</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We work with trusted third-party service providers who help us operate our platform:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
              <li><strong>Payment Processors:</strong> Stripe and other payment services</li>
              <li><strong>Cloud Services:</strong> Data hosting and storage providers</li>
              <li><strong>Analytics:</strong> Usage tracking and performance monitoring</li>
              <li><strong>Communication:</strong> Email and messaging services</li>
              <li><strong>Security:</strong> Fraud detection and prevention services</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-900 mb-3">4.3 Legal Requirements</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may disclose your information when required by law, legal process, or government request, 
              or to protect our rights, property, or safety.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">4.4 Business Transfers</h3>
            <p className="text-gray-700 leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred 
              as part of that transaction.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Lock size={24} className="text-blue-600" />
              5. Data Security
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Technical Safeguards</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                  <li>SSL/TLS encryption</li>
                  <li>Secure data centers</li>
                  <li>Regular security audits</li>
                  <li>Access controls</li>
                  <li>Data backup systems</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Operational Safeguards</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                  <li>Employee training</li>
                  <li>Privacy policies</li>
                  <li>Incident response procedures</li>
                  <li>Regular updates</li>
                  <li>Third-party assessments</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
              <p className="text-sm text-gray-700">
                <strong>Important:</strong> While we strive to protect your information, no security system is impenetrable. 
                We cannot guarantee absolute security of your data.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-medium text-gray-900">Access and Portability</h4>
                <p className="text-gray-700 text-sm">Request a copy of your personal information and data portability.</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-medium text-gray-900">Correction</h4>
                <p className="text-gray-700 text-sm">Update or correct inaccurate personal information.</p>
              </div>
              <div className="border-l-4 border-yellow-500 pl-4">
                <h4 className="font-medium text-gray-900">Deletion</h4>
                <p className="text-gray-700 text-sm">Request deletion of your personal information (subject to legal requirements).</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-medium text-gray-900">Opt-Out</h4>
                <p className="text-gray-700 text-sm">Unsubscribe from marketing communications and data processing.</p>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise your rights, please contact us using the information provided below. 
              We will respond to your request within 30 days.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your personal information for as long as necessary to provide our services and fulfill legal obligations:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Account Information:</strong> Until account deletion plus 7 years for legal compliance</li>
              <li><strong>Transaction Records:</strong> 7 years for tax and regulatory requirements</li>
              <li><strong>Marketing Data:</strong> Until you opt-out or 3 years of inactivity</li>
              <li><strong>Support Communications:</strong> 3 years for quality assurance</li>
              <li><strong>Analytics Data:</strong> Aggregated data may be retained indefinitely</li>
            </ul>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws than your country.
            </p>
            <p className="text-gray-700 leading-relaxed">
              When we transfer your information internationally, we implement appropriate safeguards such as 
              standard contractual clauses and adequacy decisions.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our Service is not intended for children under 18 years of age. We do not knowingly collect 
              personal information from children under 18.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If you are a parent or guardian and believe your child has provided us with personal information, 
              please contact us immediately so we can delete such information.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our Service may contain links to third-party websites or services. We are not responsible for 
              the privacy practices of these third parties.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We encourage you to read the privacy policies of any third-party sites you visit.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
              <li>Posting the updated policy on our website</li>
              <li>Sending an email notification</li>
              <li>Displaying a prominent notice on our platform</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of the Service after any changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="space-y-3 text-gray-700">
              <div className="flex items-center gap-2">
                <Mail size={16} />
                <span><strong>Privacy Officer:</strong> privacy@evoltbay.com</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={16} />
                <span><strong>General Inquiries:</strong> legal@evoltbay.com</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={16} />
                <span><strong>Phone:</strong> +1 (555) 123-4567</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                <span><strong>Address:</strong> 123 Solar Street, Green City, GC 12345</span>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Data Protection Officer (EU/UK)</h4>
              <p className="text-gray-700 text-sm">
                If you are located in the European Union or United Kingdom, you may contact our 
                Data Protection Officer at: dpo@evoltbay.com
              </p>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              © 2024 Evoltbay Inc. All rights reserved. | 
              <Link to="/terms" className="text-blue-600 hover:text-blue-800 ml-1">Terms of Service</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage; 